/* eslint-disable max-lines */
import { apolloClient } from '@/apollo';
import { codeOccurrenceReactive, quotationSourcesReactive, computeCodeOccurrences } from '@/apollo/cache';

import { reject, pick } from 'ramda';
import {
  AddCodesToQuotationDocument,
  AddCodesToQuotationMutation,
  AddCodesToQuotationMutationVariables,
  CodeDefaultFragment,
  CreateQuotationDocument,
  CreateQuotationMutation,
  CreateQuotationMutationVariables,
  CreateQuotationsDocument,
  CreateQuotationsMutation,
  CreateQuotationsMutationVariables,
  DeleteQuotationDocument,
  DeleteQuotationMutation,
  DeleteQuotationMutationVariables,
  ProjectDocument,
  ProjectQuery,
  ProjectQueryVariables,
  RemoveCodeFromQuotationDocument,
  RemoveCodeFromQuotationMutation,
  RemoveCodeFromQuotationMutationVariables,
  RestoreCodeToQuotationDocument,
  RestoreCodeToQuotationMutation,
  RestoreCodeToQuotationMutationVariables,
  RestoreQuotationDocument,
  RestoreQuotationMutation,
  RestoreQuotationMutationVariables,
  UpdateQuotationDocument,
  UpdateQuotationMutation,
  UpdateQuotationMutationVariables,
  UserActivityFragment,
  UserDocument,
  QuotationCodeUpdateFragmentDoc,
  ProjectCodeFragmentDoc,
  SourceQuotationUpdateFragmentDoc,
  QuotationInput,
} from '@/graphql/operations.gen';
import { allToPromise } from './allToPromise';
import { updateRecentCodes } from './updateRecentCodes';
import { ApolloClient } from '@apollo/client/core';
import { ProjectQuotationCodeAttachmentCode } from '@/types';

export type QuotationCodeUpdateResult = {
  codeData: {
    codesAdded: AddCodesToQuotationMutation['addCodesToQuotation'];
    codesRemoved: Array<RemoveCodeFromQuotationMutation['removeCodeFromQuotation']>;
  };
};

type QuotationCodeUpdateInput = {
  quotation: {
    id: string;
  };

  codeData: {
    codesToAdd: Array<string>;
    codesToRemove: Array<string>;
  };
  projectId?: string;
};

const updateQuotationInCache = (cache: ApolloClient<any>, { id }, updateFn) => {
  const cachedQuotation = cache.readFragment({
    id: `Quotation:${id}`, // `id` is any id that could be returned by `dataIdFromObject`.
    fragment: QuotationCodeUpdateFragmentDoc,
    fragmentName: 'QuotationCodeUpdate',
  });

  cache.writeFragment({
    id: `Quotation:${id}`,
    fragment: QuotationCodeUpdateFragmentDoc,
    fragmentName: 'QuotationCodeUpdate',
    data: { ...cachedQuotation, ...updateFn(cachedQuotation) },
  });
};

/**
 * Update the global occurrence value of a code and its parent
 *
 */
const updateOccurrences = (code: Omit<ProjectQuotationCodeAttachmentCode, 'occurrence' | 'groups'>, value: number) => {
  const occurrences = codeOccurrenceReactive();

  occurrences[code.id] = occurrences[code.id] || 0;

  occurrences[code.id] += value;

  if (code.parent) {
    occurrences[code.parent.id] += value;
  }

  codeOccurrenceReactive({ ...occurrences });
};

const updateSourceQuotationsInCache = (cache, id, updateFn) => {
  const cacheId = `Source:${id}`;

  const cacheData = cache.readFragment({
    id: cacheId,
    fragment: SourceQuotationUpdateFragmentDoc,
    fragmentName: 'SourceQuotationUpdate',
  });

  cache.writeFragment({
    id: cacheId,
    fragment: SourceQuotationUpdateFragmentDoc,
    fragmentName: 'SourceQuotationUpdate',
    data: { ...cacheData, ...updateFn(cacheData) },
  });
};

const getUserActivityData = () => {
  const result = apolloClient.readQuery({
    query: UserDocument,
  });

  return {
    __typename: 'User' as const,
    id: result.user.id,
    firstname: result.user.firstname,
    lastname: result.user.lastname,
    fullname: result.user.fullname,
    image: result.user.image,
    email: result.user.email,
    isPlaceholder: false,
  };
};

const getCode = (cache, id: string) =>
  cache.readFragment({
    id: `Code:${id}`, // `id` is any id that could be returned by `dataIdFromObject`.
    fragment: ProjectCodeFragmentDoc,
    fragmentName: 'ProjectCode',
  });

const addCodes = ({
  quotationId,
  codeIds,
  projectId,
}: {
  quotationId: string;
  codeIds: Array<string>;
  projectId?: string;
}) =>
  apolloClient
    .mutate<AddCodesToQuotationMutation, AddCodesToQuotationMutationVariables>({
      mutation: AddCodesToQuotationDocument,
      variables: {
        codeIds,
        quotationId,
      },
      optimisticResponse: {
        addCodesToQuotation: codeIds.map((codeId) => ({
          __typename: 'CodeAttachment',
          quotationId,
          attachedAt: new Date(),
          attachedBy: getUserActivityData(),
          code: getCode(apolloClient.cache, codeId),
        })),
      },
      update(_store, response) {
        // update the project's most recently used codes
        // in the local config
        // Do this in the update fn so that the next apollo cache read already has the new values
        if (projectId) {
          updateRecentCodes(projectId, codeIds);
        }

        const added = response.data.addCodesToQuotation;
        if (added) {
          updateQuotationInCache(apolloClient, { id: quotationId }, (cachedQuotation) => {
            const quotation = {
              ...cachedQuotation,
              updatedAt: added[0].attachedAt,
              updatedBy: added[0].attachedBy,
              codes: [],
            };

            const codeAttachments = added.map((attachment) => ({
              __typename: 'CodeAttachment',
              code: attachment.code,
              attachedAt: attachment.attachedAt,
              attachedBy: attachment.attachedBy,
              quotationId,
            }));

            quotation.codes = [...cachedQuotation.codes, ...codeAttachments];

            return quotation;
          });
        }
      },
    })
    .then((response) => {
      if (response.data) {
        response.data.addCodesToQuotation.forEach((codeAttachment) => {
          updateOccurrences(codeAttachment.code, 1);
        });
      }

      return response.data.addCodesToQuotation;
    });

const removeCode = ({ codeId, quotationId }: { codeId: string; quotationId: string }) =>
  apolloClient
    .mutate<RemoveCodeFromQuotationMutation, RemoveCodeFromQuotationMutationVariables>({
      mutation: RemoveCodeFromQuotationDocument,
      variables: {
        codeId,
        quotationId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removeCodeFromQuotation: {
          __typename: 'CodeAttachment',
          quotationId,
          code: getCode(apolloClient.cache, codeId),
        },
      },
      update() {
        updateQuotationInCache(apolloClient, { id: quotationId }, (cachedQuotation) => ({
          updatedAt: new Date(),
          updatedBy: getUserActivityData(),
          codes: reject((code) => code.code.id === codeId)(cachedQuotation.codes),
        }));
      },
    })
    .then((response) => {
      if (response.data) {
        updateOccurrences(response.data.removeCodeFromQuotation.code, -1);
      }

      return response.data.removeCodeFromQuotation;
    });

const update = (data: UpdateQuotationMutationVariables) =>
  apolloClient
    .mutate<UpdateQuotationMutation, UpdateQuotationMutationVariables>({
      mutation: UpdateQuotationDocument,
      variables: data,
    })
    .then((response) => response.data.updateQuotation);

const updateCodes = async (
  quotation: { id: string },
  codeData: QuotationCodeUpdateInput['codeData'],
  projectId: string
) => {
  const { codesToAdd, codesToRemove } = codeData;

  // in parallel:
  // - remove codes
  // - add existing codes

  const removeCodes = allToPromise<string, ReturnType<typeof removeCode>>(codesToRemove, (code) =>
    removeCode({
      quotationId: quotation.id,
      codeId: code,
    })
  );

  const addCodesRequest = codesToAdd.length
    ? addCodes({ quotationId: quotation.id, codeIds: codesToAdd, projectId })
    : [];

  const codeResponses = await Promise.all([removeCodes, addCodesRequest]);

  return {
    added: codeResponses[1],
    removed: codeResponses[0],
  };
};

export const restoreCodeToQuotation = ({
  code,
  quotationId,
  attachedAt,
  attachedBy,
}: {
  code: CodeDefaultFragment;
  quotationId: string;
  attachedAt: string;
  attachedBy: UserActivityFragment;
}) =>
  apolloClient
    .mutate<RestoreCodeToQuotationMutation, RestoreCodeToQuotationMutationVariables>({
      mutation: RestoreCodeToQuotationDocument,
      variables: {
        attachedAt,
        quotationId,
        attachedByUserId: attachedBy && attachedBy.id,
        codeId: code.id,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        restoreCodeToQuotation: {
          __typename: 'Quotation',
          id: quotationId,
          updatedAt: new Date(),
          updatedBy: getUserActivityData(),
        },
      },
      update(_store, response) {
        updateQuotationInCache(apolloClient, { id: quotationId }, (cachedQuotation) => ({
          updatedAt: response.data.restoreCodeToQuotation.updatedAt,
          updatedBy: response.data.restoreCodeToQuotation.updatedBy,
          codes: [
            ...cachedQuotation.codes,
            {
              __typename: 'CodeAttachment',
              code,
              attachedAt,
              attachedBy,
              quotationId,
            },
          ],
        }));
      },
    })
    .then((response) => {
      if (response.data) {
        updateOccurrences(code, 1);
      }

      return response;
    });

export const createQuotation = (data: CreateQuotationMutationVariables) =>
  apolloClient
    .mutate<CreateQuotationMutation, CreateQuotationMutationVariables>({
      mutation: CreateQuotationDocument,
      variables: pick(['sourceId', 'name', 'from', 'to', 'comment', 'containsImage'], data),
      optimisticResponse: {
        __typename: 'Mutation',
        createQuotation: {
          __typename: 'Quotation',
          id: `${data.from}-${data.to}`, // Preliminary id - must be unique, so we can use the range
          name: data.name,
          comment: data.comment || '',
          containsImage: data.containsImage || false,
          from: data.from,
          to: data.to,
          deletedAt: null,
          createdAt: new Date().toISOString(),
          createdBy: getUserActivityData(),
          updatedAt: new Date().toISOString(),
          updatedBy: getUserActivityData(),
          codes: [],
          codeCount: 0,
        },
      },
      update(cache, response) {
        const quotationSources = quotationSourcesReactive();

        quotationSources.set(response.data.createQuotation.id, data.sourceId);

        quotationSourcesReactive(new Map(quotationSources));

        updateSourceQuotationsInCache(cache, data.sourceId, (cachedQuotation) => ({
          quotations: [...cachedQuotation.quotations, response.data.createQuotation],
        }));

        return response;
      },
    })
    .then((response) => response.data.createQuotation);

export const deleteQuotation = ({
  id,
  sourceId,
  projectId,
}: DeleteQuotationMutationVariables & { sourceId?: string; projectId?: string }) =>
  apolloClient
    .mutate<DeleteQuotationMutation, DeleteQuotationMutationVariables>({
      mutation: DeleteQuotationDocument,
      variables: {
        id,
      },
      update(cache, response) {
        if (sourceId) {
          updateSourceQuotationsInCache(cache, sourceId, (cacheData) => ({
            quotations: reject((quotation) => quotation.id === id, cacheData.quotations),
          }));
        }

        if (projectId) {
          const { project } = cache.readQuery<ProjectQuery, ProjectQueryVariables>({
            query: ProjectDocument,
            variables: {
              id: projectId,
            },
          });

          computeCodeOccurrences(project);
        }

        return response;
      },
    })
    .then((response) => response.data.deleteQuotation);

export const removeCodeFromQuotation = ({ codeId, quotationId }: { codeId: string; quotationId: string }) =>
  removeCode({
    codeId,
    quotationId,
  });

export const restoreQuotation = ({
  id,
  from = undefined,
  to = undefined,
  sourceId,
  projectId,
}: RestoreQuotationMutationVariables & { sourceId?: string; projectId?: string }) =>
  apolloClient.mutate<RestoreQuotationMutation, RestoreQuotationMutationVariables>({
    mutation: RestoreQuotationDocument,
    variables: {
      id,
      from,
      to,
    },
    update(cache, response) {
      if (sourceId) {
        updateSourceQuotationsInCache(cache, sourceId, (cacheData) => ({
          quotations: [...cacheData.quotations, response.data.restoreQuotation],
        }));
      }

      if (projectId) {
        const { project } = cache.readQuery<ProjectQuery, ProjectQueryVariables>({
          query: ProjectDocument,
          variables: {
            id: projectId,
          },
        });

        computeCodeOccurrences(project);
      }

      return response;
    },
  });

export const updateQuotation = (data: UpdateQuotationMutationVariables) => update(data);

export const updateQuotationOnlyCodes = ({ quotation, codeData, projectId }: QuotationCodeUpdateInput) =>
  updateCodes(quotation, codeData, projectId);

export const updateQuotationWithCodes = async ({
  quotation,
  codeData,
  projectId,
}: QuotationCodeUpdateInput): Promise<QuotationCodeUpdateResult> => {
  if (Object.keys(quotation).length !== 1) {
    // Don't update quotation if only a single key (id) is set
    await update(quotation);
  }

  const { added, removed } = await updateCodes(quotation, codeData, projectId);

  return {
    codeData: {
      codesAdded: added,
      codesRemoved: removed,
    },
  };
};

export const createQuotations = (data: Array<QuotationInput>) =>
  apolloClient
    .mutate<CreateQuotationsMutation, CreateQuotationsMutationVariables>({
      mutation: CreateQuotationsDocument,
      variables: {
        quotations: data.map((quotationInput) =>
          pick(['sourceId', 'name', 'from', 'to', 'comment', 'containsImage'], quotationInput)
        ),
      },
      optimisticResponse: {
        __typename: 'Mutation',
        createQuotations: data.map((quotationInput) => ({
          __typename: 'Quotation',
          sourceId: quotationInput.sourceId,
          id: `${quotationInput.from}-${quotationInput.to}`, // Preliminary id - must be unique, so we can use the range
          name: quotationInput.name,
          comment: quotationInput.comment || '',
          containsImage: quotationInput.containsImage || false,
          from: quotationInput.from,
          to: quotationInput.to,
          deletedAt: null,
          createdAt: new Date().toISOString(),
          createdBy: getUserActivityData(),
          updatedAt: new Date().toISOString(),
          updatedBy: getUserActivityData(),
          codes: [],
          codeCount: 0,
        })),
      },
      update(cache, response) {
        const quotationSources = quotationSourcesReactive();

        for (const createdQuotation of response.data.createQuotations) {
          quotationSources.set(createdQuotation.id, createdQuotation.sourceId);
        }

        quotationSourcesReactive(new Map(quotationSources));

        for (const createdQuotation of response.data.createQuotations) {
          updateSourceQuotationsInCache(cache, createdQuotation.sourceId, (cachedQuotation) => ({
            quotations: [...cachedQuotation.quotations, createdQuotation],
          }));
        }

        return response;
      },
    })
    .then((response) => response.data.createQuotations);
